import React from "react";
import { motion } from "framer-motion";
import Container from "components/Container";
import CreateAccountModal from "components/CreateAccountModal";
import DownloadModal from "components/DownloadModal";
import { useSignupForm } from "hooks/useSignupForm";
import TurnstileWidget, {
  TurnstileWidgetHandle,
} from "components/TurnstileWidget";

import styles from "./styles.module.scss";

import LogoWinAZ from "assets/img-winaz/logo.webp";
import TypoWinAZ from "assets/img-winaz/typo.webp";
import BackgroundDesktop from "assets/img-winaz/background/bgwweb2.webp";
import BackgroundMobile from "assets/img-winaz/background/bg.webp";
import BackgroundBottom from "assets/img-winaz/background/bgbottom.webp";
import BackgroundGirl from "assets/img-winaz/background/gai.webp";
import BackgroundMoney from "assets/img-winaz/background/money.webp";
import ButtonRegister from "assets/img-winaz/button/btndawng ki.webp";
import Label29k from "assets/img-winaz/button/label29k.webp";
import ButtonDownload from "assets/img-winaz/button/btntaigame.webp";
import HeroBannerMobile1 from "assets/img-winaz/banner-moblie/bannermobile1.webp";
import HeroBannerMobile2 from "assets/img-winaz/banner-moblie/bannermobile2.webp";
import HeroBannerMobile3 from "assets/img-winaz/banner-moblie/bannermobile3.webp";
import HeroBannerMobile4 from "assets/img-winaz/banner-moblie/bannermobile4.webp";
import HeroBannerMobile5 from "assets/img-winaz/banner-moblie/bannermobile5.webp";
import InputBackground from "assets/img-winaz/signup-form/input.webp";
import IconUser from "assets/img-winaz/signup-form/iconusser.webp";
import IconPassword from "assets/img-winaz/signup-form/iconpassword.webp";
import FormBackground from "assets/img-winaz/signup-form/bgpopup.webp";
import SocialFacebook from "assets/img-winaz/social-icon/001-facebook.webp";
import SocialTelegram from "assets/img-winaz/social-icon/002-telegram.webp";
import { getPlayGameDomain } from "utils";
import { SOCIAL_LINKS } from "const";

declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

type SliderItem = {
  id: number;
  src: string;
  alt: string;
};

type SocialLink = {
  id: string;
  icon: string;
  alt: string;
  url: string;
  target?: "_self" | "_blank";
};

const HERO_MOBILE_BANNERS: SliderItem[] = [
  { id: 0, src: HeroBannerMobile1, alt: "WinAZ khuyến mãi 1" },
  { id: 1, src: HeroBannerMobile2, alt: "WinAZ khuyến mãi 2" },
  { id: 2, src: HeroBannerMobile3, alt: "WinAZ khuyến mãi 3" },
  { id: 3, src: HeroBannerMobile4, alt: "WinAZ khuyến mãi 4" },
  { id: 4, src: HeroBannerMobile5, alt: "WinAZ khuyến mãi 5" },
];

const SOCIAL_ITEMS: SocialLink[] = [
  {
    id: "facebook",
    icon: SocialFacebook,
    alt: "Facebook",
    url: SOCIAL_LINKS.facebook,
    target: "_blank",
  },
  {
    id: "telegram",
    icon: SocialTelegram,
    alt: "Telegram",
    url: SOCIAL_LINKS.telegram,
    target: "_blank",
  },
];

const INTERCOM_APP_ID = "snx821c4";

type IntercomModule = typeof import("@intercom/messenger-js-sdk");

const MOBILE_BANNER_SWIPE_THRESHOLD = 40;

const INTERACTIVE_IMAGE_PROPS = {
  initial: { filter: "brightness(1)", scale: 1 },
  whileHover: { filter: "brightness(1.08)", scale: 1.03 },
  whileTap: { scale: 0.96 },
  transition: {
    type: "spring" as const,
    stiffness: 450,
    damping: 32,
    mass: 0.28,
  },
};

const ShortLanding: React.FunctionComponent = () => {
  const [activeMobileBanner, setActiveMobileBanner] = React.useState(0);
  const [isCreateAccountModalOpen, setIsCreateAccountModalOpen] =
    React.useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = React.useState(false);
  const intercomModuleRef = React.useRef<Promise<IntercomModule | null> | null>(
    null
  );
  const mobileTouchStartXRef = React.useRef<number | null>(null);
  const mobileTouchCurrentXRef = React.useRef<number | null>(null);
  const turnstileWidgetRef = React.useRef<TurnstileWidgetHandle | null>(null);
  const hasTrackedInlineFormStartRef = React.useRef(false);
  const mobileAutoSlideTimerRef = React.useRef<number | null>(null);

  const handleResetTurnstile = React.useCallback(() => {
    turnstileWidgetRef.current?.reset();
  }, []);

  const pushDataLayerEvent = React.useCallback(
    (eventName: string, payload: Record<string, any> = {}) => {
      if (typeof window === "undefined") {
        return;
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: eventName,
        ...payload,
      });
    },
    []
  );

  const trackInlineFormStart = React.useCallback(() => {
    if (hasTrackedInlineFormStartRef.current) {
      return;
    }

    hasTrackedInlineFormStartRef.current = true;
    pushDataLayerEvent("lp_form_start", { form_id: "register_inline" });
  }, [pushDataLayerEvent]);

  const {
    username,
    password,
    apiError,
    usernameError,
    passwordError,
    isSubmitting,
    turnstileRequired,
    handleSubmit: handleInlineSubmit,
    handleUsernameChange,
    handlePasswordChange,
    handleTurnstileTokenChange,
  } = useSignupForm({
    requireConfirmPassword: false,
    formId: "register_inline",
    onTurnstileReset: handleResetTurnstile,
  });

  const loadIntercom = React.useCallback(() => {
    if (typeof window === "undefined") {
      return Promise.resolve(null);
    }

    if (!intercomModuleRef.current) {
      intercomModuleRef.current = import("@intercom/messenger-js-sdk")
        .then((module) => {
          module.default({
            app_id: INTERCOM_APP_ID,
          });
          return module;
        })
        .catch((error) => {
          console.error("Failed to load Intercom messenger", error);
          intercomModuleRef.current = null;
          return null;
        });
    }

    return intercomModuleRef.current;
  }, []);

  React.useEffect(() => {
    loadIntercom();
  }, [loadIntercom]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    HERO_MOBILE_BANNERS.forEach(({ src }) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const handleDownloadClick = React.useCallback(() => {
    pushDataLayerEvent("lp_click_button", { button_id: "download" });
    setIsDownloadModalOpen(true);
  }, [pushDataLayerEvent]);


  const handleRegisterCtaClick = React.useCallback(
    (location: string) => {
      pushDataLayerEvent("lp_click_cta_register", { location });
      setIsCreateAccountModalOpen(true);
    },
    [pushDataLayerEvent]
  );

  const handleLogoClick = React.useCallback(() => {
    pushDataLayerEvent("lp_click_logo", { location: "header" });
  }, [pushDataLayerEvent]);

  const handleSocialClick = React.useCallback(
    (id: string, url: string, target: "_self" | "_blank", location: string) => {
      pushDataLayerEvent("lp_click_social", { social_id: id, location });
      if (typeof window !== "undefined") {
        window.open(url, target);
      }
    },
    [pushDataLayerEvent]
  );

  const handleSelectMobileBanner = React.useCallback((index: number) => {
    setActiveMobileBanner(index);
    if (mobileAutoSlideTimerRef.current) {
      window.clearTimeout(mobileAutoSlideTimerRef.current);
    }
    mobileAutoSlideTimerRef.current = window.setTimeout(() => {
      setActiveMobileBanner((prev) => (prev + 1) % HERO_MOBILE_BANNERS.length);
    }, 5000);
  }, []);

  const handleMobileTouchStart = React.useCallback(
    (event: React.TouchEvent) => {
      mobileTouchStartXRef.current = event.touches[0]?.clientX ?? null;
      if (mobileAutoSlideTimerRef.current) {
        window.clearTimeout(mobileAutoSlideTimerRef.current);
      }
    },
    []
  );

  const handleMobileTouchMove = React.useCallback((event: React.TouchEvent) => {
    if (mobileTouchStartXRef.current === null) {
      return;
    }
    mobileTouchCurrentXRef.current = event.touches[0]?.clientX ?? null;
  }, []);

  const handleMobileTouchEnd = React.useCallback(() => {
    if (
      mobileTouchStartXRef.current === null ||
      mobileTouchCurrentXRef.current === null
    ) {
      return;
    }

    const delta = mobileTouchStartXRef.current - mobileTouchCurrentXRef.current;

    if (Math.abs(delta) > MOBILE_BANNER_SWIPE_THRESHOLD) {
      setActiveMobileBanner((prev) => {
        const total = HERO_MOBILE_BANNERS.length;
        return delta > 0 ? (prev + 1) % total : (prev - 1 + total) % total;
      });
    }

    mobileTouchStartXRef.current = null;
    mobileTouchCurrentXRef.current = null;

    mobileAutoSlideTimerRef.current = window.setTimeout(() => {
      setActiveMobileBanner((prev) => (prev + 1) % HERO_MOBILE_BANNERS.length);
    }, 5000);
  }, []);

  const handleMobileTouchCancel = React.useCallback(() => {
    mobileTouchStartXRef.current = null;
    mobileTouchCurrentXRef.current = null;
  }, []);

  React.useEffect(() => {
    mobileAutoSlideTimerRef.current = window.setTimeout(() => {
      setActiveMobileBanner((prev) => (prev + 1) % HERO_MOBILE_BANNERS.length);
    }, 5000);

    return () => {
      if (mobileAutoSlideTimerRef.current) {
        window.clearTimeout(mobileAutoSlideTimerRef.current);
      }
    };
  }, []);

  return (
    <div
      className={styles.slider}
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.05) 100%), url(${BackgroundDesktop})`,
        backgroundSize: "cover",
        backgroundPosition: "center top",
        backgroundRepeat: "no-repeat",
      }}
    >
      <img
        className={styles.backgroundMobile}
        src={BackgroundMobile}
        alt=""
        aria-hidden="true"
      />
      <img
        className={styles.backgroundBottom}
        src={BackgroundBottom}
        alt=""
        aria-hidden="true"
      />
      <img
        className={styles.backgroundMoney}
        src={BackgroundMoney}
        alt=""
        aria-hidden="true"
      />

      <section id="section-hero" className={`${styles.slide} ${styles.hero}`}>
        <div className={styles.heroFigureWrapper}>
          <img className={styles.heroFigure} src={BackgroundGirl} alt="" />
        </div>
        <Container className={styles.heroContainer}>
          <div className={styles.heroContent}>
            <button
              type="button"
              id="logo"
              className={`${styles.logoButton} ${styles.logoPrimary}`}
              onClick={handleLogoClick}
              aria-label="WinAZ"
            >
              <img src={LogoWinAZ} alt="WinAZ" />
            </button>
            <button
              type="button"
              className={`${styles.logoButton} ${styles.logoTypo}`}
              onClick={handleLogoClick}
              aria-label="WinAZ.com - Đẳng cấp thượng lưu"
            >
              <img src={TypoWinAZ} alt="WinAZ.com - Đẳng cấp thượng lưu" />
            </button>

            <div
              className={styles.heroMobileBanner}
              onTouchStart={handleMobileTouchStart}
              onTouchMove={handleMobileTouchMove}
              onTouchEnd={handleMobileTouchEnd}
              onTouchCancel={handleMobileTouchCancel}
            >
              {HERO_MOBILE_BANNERS.map((item, index) => (
                <img
                  key={item.id}
                  src={item.src}
                  alt={item.alt}
                  aria-hidden={
                    index === activeMobileBanner ? undefined : "true"
                  }
                  className={
                    index === activeMobileBanner
                      ? styles.heroMobileBannerImageActive
                      : styles.heroMobileBannerImage
                  }
                  draggable={false}
                />
              ))}
              <div className={styles.heroMobileIndicators}>
                {HERO_MOBILE_BANNERS.map((item, index) => (
                  <button
                    key={item.id}
                    type="button"
                    aria-label={`Chọn banner ${index + 1}`}
                    className={
                      index === activeMobileBanner
                        ? styles.heroMobileDotActive
                        : styles.heroMobileDot
                    }
                    onClick={() => handleSelectMobileBanner(index)}
                  />
                ))}
              </div>
            </div>

            <div className={styles.heroFormCluster}>
              <form
                className={styles.loginCard}
                style={{ backgroundImage: `url(${FormBackground})` }}
                onSubmit={handleInlineSubmit}
              >
                <div className={styles.loginFields}>
                  <div className={styles.fieldGroup}>
                    <label
                      className={styles.inputWrapper}
                      style={{ backgroundImage: `url(${InputBackground})` }}
                    >
                      <img src={IconUser} alt="" aria-hidden="true" />
                      <input
                        type="text"
                        placeholder="Tên đăng nhập"
                        value={username}
                        onChange={handleUsernameChange}
                        onFocus={trackInlineFormStart}
                        disabled={isSubmitting}
                      />
                    </label>
                    {usernameError ? (
                      <div className={styles.inlineError} aria-live="assertive">
                        Vui lòng nhập tên đăng nhập
                      </div>
                    ) : null}
                  </div>
                  <div className={styles.fieldGroup}>
                    <label
                      className={styles.inputWrapper}
                      style={{ backgroundImage: `url(${InputBackground})` }}
                    >
                      <img src={IconPassword} alt="" aria-hidden="true" />
                      <input
                        type="password"
                        placeholder="Mật khẩu"
                        value={password}
                        onChange={handlePasswordChange}
                        onFocus={trackInlineFormStart}
                        disabled={isSubmitting}
                      />
                    </label>
                    {passwordError ? (
                      <div className={styles.inlineError} aria-live="assertive">
                        Vui lòng nhập mật khẩu
                      </div>
                    ) : null}
                  </div>
                  {turnstileRequired ? (
                    <div className={styles.turnstileGroup}>
                      <div className={styles.turnstileBlock}>
                        <div className={styles.turnstileInner}>
                          <TurnstileWidget
                            ref={turnstileWidgetRef}
                            className={styles.turnstileWidget}
                            onTokenChange={handleTurnstileTokenChange}
                          />
                        </div>
                      </div>
                    </div>
                  ) : null}
                  {apiError ? (
                    <div className={styles.inlineError} aria-live="assertive">
                      {apiError}
                    </div>
                  ) : null}
                </div>

                <button
                  type="submit"
                  onClick={() =>
                    pushDataLayerEvent("lp_click_cta_register", {
                      location: "hero_desktop",
                    })
                  }
                  className={styles.registerButton}
                  disabled={isSubmitting}
                  data-cta
                >
                  <img
                    src={Label29k}
                    alt="29k"
                    className={styles.registerLabel}
                  />
                  <motion.img
                    {...INTERACTIVE_IMAGE_PROPS}
                    src={ButtonRegister}
                    alt="Đăng ký"
                  />
                </button>
              </form>

              <div className={styles.heroActions}>
                <button
                  type="button"
                  className={`${styles.heroActionButton} ${styles.heroDownloadButton}`}
                  id="btn-download"
                  onClick={handleDownloadClick}
                >
                  <motion.img
                    {...INTERACTIVE_IMAGE_PROPS}
                    src={ButtonDownload}
                    alt="Tải game"
                  />
                </button>
              </div>

              <div className={styles.heroSocials}>
                {SOCIAL_ITEMS.map((item) => (
                  <button
                    key={item.id}
                    id={`social-${item.id}`}
                    type="button"
                    className={`social_platform_${item.id}`}
                    onClick={() =>
                      handleSocialClick(
                        item.id,
                        item.url,
                        item.target ?? "_self",
                        "hero"
                      )
                    }
                  >
                    <img src={item.icon} alt={item.alt} />
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              className={styles.registerButtonMobile}
              onClick={() => handleRegisterCtaClick("hero_mobile")}
            >
              <img
                src={Label29k}
                alt="29k"
                className={styles.registerLabel}
              />
              <motion.img
                {...INTERACTIVE_IMAGE_PROPS}
                src={ButtonRegister}
                alt="Đăng ký"
              />
            </button>
          </div>
        </Container>
      </section>

      <CreateAccountModal
        isOpen={isCreateAccountModalOpen}
        onClose={() => setIsCreateAccountModalOpen(false)}
      />
      <DownloadModal
        isOpen={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
      />
    </div>
  );
};

export default ShortLanding;
